import React, { useRef, useState } from 'react'
import {
	Border,
	CalendarToday,
	CardBox,
	CardHeaderRow,
	CardMenuButton,
	CardMenuItem,
	CardMeta,
	CardTitle,
	CardWrap,
	CategoryWapper,
	MenuItemWrapper,
} from '../styles'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DropIndicator } from '../Helpers'
import { Chip, Select, TOAST_TYPE } from '@base'
import { Tooltip } from '@baseV2'
import { getLeadRolesText } from '@pages/Admin/Lead/leadRoles'
import { LightTheme } from '@styles'
import calendarIcon from '@assets/icons/calendar.svg'
import locationIcon from '@assets/icons/location.svg'
import mobileIcon from '@assets/icons/mobile.svg'
import StatusBadge from './StatusBadge'
import { useDispatch } from 'react-redux'
import { showLoader, showToast } from '@data/state/action'
import { assignLeadManager } from '@data/conn/put/leads'
import { LEADS_SOURCE, MODAL_TYPES } from '@constants'
import { toggleModal } from '@data/state/action/root'
import { useNavigate } from 'react-router'
import { paths } from '@routes'
import { showDateFormat } from '@utils'

export const categoryText = (data) => {
	if (Array.isArray(data?.category_items) && data.category_items.length) {
		return (
			<CategoryWapper>
				{data.category_items
					.map((x) => x?.category_name || x?.commodity_name)
					.filter(Boolean)
					?.map((item) => (
						<Chip label={item} status='custom' background={LightTheme.palette.text.pink} color={LightTheme.palette.text.primary} />
					))}
			</CategoryWapper>
		)
	}
	return '-'
}

const SOURCE_LABEL_OVERRIDE = {
	COLD_CALLING: 'COLD CALL',
	GOVT_TENDER_DATABASE: 'TENDER DB',
	MetalOps_App: 'MB App',
}

export const getSourceLabel = (source) => SOURCE_LABEL_OVERRIDE[source] || LEADS_SOURCE[source] || source

const Card = ({ id, data, showIndicatorAbove, salesAdmin, update }) => {
	const dispatch = useDispatch()
	const [saleAdmin, setSaleAdmin] = useState({})
	const isInteractingRef = useRef(false)
	const navigate = useNavigate()

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id,
		data: { type: 'card' },
	})

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.35 : 1,
		transformOrigin: 'top left',
	}

	const salesPersonHandler = (user) => {
		dispatch(
			toggleModal(true, MODAL_TYPES.CONFIRMATION, {
				title: `Are you sure you want to assign ${user?.full_name || 'Salesperson'}?`,
				confirmAction: (flag) => {
					if (flag) {
						setSaleAdmin(user)
						dispatch(showLoader(true))
						assignLeadManager(data?.lead_id, user?.user_id)
							.then((res) => {
								dispatch(showToast(true, 'Salesperson updated successfully', { type: TOAST_TYPE.SUCCESS }))
							})
							.catch((error) => {
								dispatch(showToast(true, error?.response?.data?.message, { type: TOAST_TYPE.ERROR }))
							})
							.finally(() => {
								dispatch(showLoader(false))
								dispatch(toggleModal(false))
								update()
							})
					} else {
						dispatch(toggleModal(false))
					}
				},
			})
		)
	}

	return (
		<CardWrap>
			<DropIndicator show={showIndicatorAbove} />

			<CardBox
				onClick={() => {
					if (isInteractingRef.current) return // ignore click from select
					navigate(paths.admin_lead_details(data?.lead_id))
				}}
				ref={setNodeRef}
				style={style}
				{...attributes}
				{...(!isInteractingRef.current ? listeners : {})}
			>
				<CardHeaderRow>
					{data?.raw?.source ? (
						<StatusBadge status={data?.raw?.source?.replace(/\s+/g, '_')} label={getSourceLabel(data?.raw?.source)} />
					) : (
						<StatusBadge status='OTHER' label='Source: -' />
					)}
					<CalendarToday>
						<img src={calendarIcon} alt='calendarIcon' />
						{data?.created_at ? showDateFormat(new Date(data?.created_at)) : '-'}
					</CalendarToday>
				</CardHeaderRow>
				<CardTitle>{data?.organisation_name}</CardTitle>
				<CardHeaderRow>
					<Tooltip content={getLeadRolesText(data?.roles) || undefined} block>
						<CardMeta>{getLeadRolesText(data?.roles) || 'Role: -'}</CardMeta>
					</Tooltip>
					<CardMeta>{data?.pan}</CardMeta>
				</CardHeaderRow>
				<Border />
				{!!data?.category_items?.length && (
					<>
						<CardMeta>{categoryText(data)}</CardMeta>
						<Border />
					</>
				)}
				<CardHeaderRow>
					<CalendarToday>
						<img src={locationIcon} alt='locationIcon' />
						{data?.location}
					</CalendarToday>
					<CalendarToday>
						<img src={mobileIcon} alt='mobileIcon' />
						{data?.raw?.user_details?.mobile_number || 'Mobile: -'}
					</CalendarToday>
				</CardHeaderRow>
				{data?.status === 'OPEN_NEW' && (
					<div
						onMouseDown={() => {
							isInteractingRef.current = true
						}}
						onMouseUp={() => {
							setTimeout(() => {
								isInteractingRef.current = false
							}, 100)
						}}
					>
						<Select
							id={'assign-sales-person-column'}
							label={'Assign Sales Person'}
							placeholder={'select sales person'}
							value={saleAdmin}
							primaryKey='user_id'
							displayKey='full_name'
							data={salesAdmin || []}
							onChange={(val) => salesPersonHandler(val)}
						/>
					</div>
				)}
			</CardBox>
		</CardWrap>
	)
}

export default React.memo(Card)
